import React, {useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {ActivityIndicator, Image, Platform, StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {observer} from 'mobx-react';

import Button from '../../components/themed/Button';
import {ErrorDialog} from '../../components/ui-controls/ErrorDialog';
import TextInput from '../../components/ui-controls/form/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Markdown from '../../components/ui-controls/Markdown';
import Text from '../../components/ui-controls/Text';
import {Title} from '../../components/ui-controls/text/Title';
import TmpDatePicker from '../../components/ui-controls/TmpDatePicker';
import config, {Brand} from '../../config';
import BannerImg from '../../images/brand/giftCardsShop/banner.png';
import {AppRoute} from '../../models/AppRoute';
import {useStores} from '../../stores/use-stores';
import {OrderLine} from '../../transport/apiServiceResponse';
import {getTodayAt0Hours} from '../../utils/date';
import {handleGiftCardShopBlurbLinkPress, makeGiftCardRecipientData} from '../../utils/giftCards';
import {interpolateMarkdownTemplate} from '../../utils/interpolateMarkdownTemplate';
import {logger} from '../../utils/logger';
import {getApiErrorMessage} from '../../utils/mfl';
import CardSelector from '../components/CardSelector';
import {GIFT_CARD_DELIVERY_TIME, MAX_CONTENT_WIDTH} from '../constants';
import giftCardShopBlurbMarkdown from '../content/giftCardShopBlurb.md';
import giftCardShopBlurbThirstyCamelMarkdown from '../content/giftCardShopBlurb.thirstycamel.md';
import {GiftCardShopRouteNavigatorScreenProps} from '../types/navigationTypes';

import {giftCardDataSchema, GiftCardFormData} from './schema';

const defaultValues: GiftCardFormData = {
  note: '',
  recipientName: '',
  recipientEmail: '',
  recipientPhoneNumber: '',
  deliveryDate: getTodayAt0Hours(),
};

const makeOrderLines = (products: OrderLine[], selectedProduct: OrderLine) => {
  const selectedProductQty1 = {
    product_id: selectedProduct.product_id,
    quantity: 1,
  };
  // We don't have a "Cart View", so in order to prevent issues when using
  // the "gift card shop" in another device, we 0 the other available products
  const restOfTheProductsQty0 = products
    .filter(orderLine => orderLine.product_id !== selectedProduct.product_id)
    .map(orderLine => ({
      product_id: orderLine.product_id,
      quantity: 0,
    }));
  const orderLines = [selectedProductQty1, ...restOfTheProductsQty0];
  return orderLines;
};

// Brand-configurable blurb copy. A brand gets its own copy by adding a
// `giftCardShopBlurb.<brand>.md` file and one entry here; brands without an
// override get the default. Placeholders are filled from config, so a new brand
// needs no component change. Mirrors GiftCardsTermsAndConditions.tsx, and moves
// with it when content configuration lands in Airtable (work item 1862).
//
// DIS-1604: only the redemption wording differs — Thirsty Camel gift cards are
// redeemable online AND in-store, so TC overrides. Every brand links to its own
// /gift-cards-participating-stores page, which each brand's web app serves.
const blurbByTenant: Partial<Record<Brand, string>> = {
  [Brand.THIRSTY_CAMEL]: giftCardShopBlurbThirstyCamelMarkdown,
};

// Blue link text. The shared Markdown component styles only `body`, so links
// would otherwise fall back to the markdown lib's default (underlined, body
// colour). Restores the appearance of the bespoke Link this screen used before
// the blurb moved to markdown content.
const markdownStyles = {link: {color: 'blue'}};

const TopSection = ({onLinkPress}: {onLinkPress: (url: string) => boolean}) => {
  const blurb = interpolateMarkdownTemplate(
    blurbByTenant[config.BASE_TENANT] ?? giftCardShopBlurbMarkdown,
    {APP_NAME: config.APP_NAME, HOST_DOMAIN: config.HOST_DOMAIN},
  );

  return (
    <>
      <Image source={BannerImg} style={styles.bannerImage} />
      <View testID="gift-card-shop-blurb" style={styles.topSectionCopy}>
        <Markdown style={markdownStyles} onLinkPress={onLinkPress}>
          {blurb}
        </Markdown>
      </View>
    </>
  );
};

export const GiftCardStoreShopScreen = observer(
  ({navigation, route}: GiftCardShopRouteNavigatorScreenProps<AppRoute.GIFT_CARDS_HOME>) => {
    const isResetForm = route?.params?.isResetForm;

    const {cartStore, rangeStore} = useStores();

    // Get initial form values from store (for Edit flow) or use defaults
    const getInitialFormValues = (): GiftCardFormData => {
      const storedValues = cartStore.zenGiftCardRecipientFormValues;
      if (storedValues && !isResetForm) {
        return storedValues;
      }
      return defaultValues;
    };

    // Get initial selected product from cart (for Edit flow)
    const getInitialSelectedProduct = (): OrderLine | null => {
      if (isResetForm) return null;
      const inCartProducts = cartStore.currentOrder?.order_lines?.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (line: any) => !line.charge,
      );
      return inCartProducts?.[0] || null;
    };

    const [selectedProduct, setSelectedProduct] = useState<OrderLine | null>(
      getInitialSelectedProduct,
    );
    const [isUpdatingRecipientData, setUpdatingRecipientData] = useState(false);
    const [submitErrorMessage, setSubmitErrorMessage] = useState('');
    const [isNativeDatePickerModalOpen, setNativeDatePickerModalOpen] = useState(false);
    const [formKey, setFormKey] = useState(0);

    // Key-based remount for form reset
    useEffect(() => {
      if (isResetForm) {
        setFormKey(prev => prev + 1);
        setSelectedProduct(null);
        navigation.setParams({isResetForm: false});
      }
    }, [isResetForm, navigation]);

    const isLoading = rangeStore.isLoadingDepartmentLines;

    const noteInput = useRef<typeof TextInput>(null);
    const emailInput = useRef<typeof TextInput>(null);
    const phoneInput = useRef<typeof TextInput>(null);

    const handleSelectProduct = async (product: OrderLine) => setSelectedProduct(product);

    const {
      handleSubmit,
      formState: {errors, touchedFields},
      setValue,
      watch,
    } = useForm<GiftCardFormData>({
      resolver: zodResolver(giftCardDataSchema),
      defaultValues: getInitialFormValues(),
      mode: 'onTouched',
    });

    const values = watch();

    const handleFieldBlur = (field: keyof GiftCardFormData) => () => {
      setValue(field, values[field], {shouldTouch: true});
    };

    const handleFormSubmit = async (formValues: GiftCardFormData) => {
      setUpdatingRecipientData(true);
      try {
        if (!selectedProduct) throw new Error('Please select a product');

        const orderLines = makeOrderLines(products, selectedProduct);
        const giftCardRecipientData = makeGiftCardRecipientData(formValues);

        await cartStore.updateOrderDirectly(orderLines);
        await cartStore.updateGiftCardRecipientDetails(giftCardRecipientData);

        cartStore.setGiftCardRecipientFormValues(formValues);
        navigation.navigate(AppRoute.GIFT_CARDS_STORE_REVIEW);
      } catch (error) {
        logger.error('GiftCardStoreShopScreen', 'Error submitting gift card form', {error});
        const errorMessage = getApiErrorMessage(error);
        setSubmitErrorMessage(errorMessage);
      } finally {
        setUpdatingRecipientData(false);
      }
    };

    const handleErrorDialogOk = () => setSubmitErrorMessage('');

    const products = rangeStore?.departmentLines || [];

    const handleBlurbLinkPress = (url: string) => handleGiftCardShopBlurbLinkPress(navigation, url);

    if (isLoading) {
      return (
        <ContentView>
          <TopSection onLinkPress={handleBlurbLinkPress} />
          <ActivityIndicator style={styles.activityIndicator} />
        </ContentView>
      );
    }

    if (!products.length) {
      return (
        <ContentView>
          <TopSection onLinkPress={handleBlurbLinkPress} />
          <Text variant="body" style={styles.topSectionCopy}>
            No gift card products are currently available. Please try again later.
          </Text>
        </ContentView>
      );
    }

    return (
      <ContentView style={{maxWidth: MAX_CONTENT_WIDTH}} testID="gift-cards-home-screen">
        <KeyboardAwareScrollView
          keyboardShouldPersistTaps="handled"
          enableOnAndroid
          extraScrollHeight={40}>
          <TopSection onLinkPress={handleBlurbLinkPress} />
          <View style={styles.cardSelector}>
            <Title>1. CHOOSE YOUR GIFT CARD VALUE</Title>
            <CardSelector
              products={products}
              selectedProduct={selectedProduct}
              onSelectProduct={handleSelectProduct}
            />
          </View>
          {!!selectedProduct && (
            <View key={formKey}>
              <Title>2. PERSONALISE YOUR GIFT</Title>
              <View style={styles.formSection}>
                <TextInput
                  accessibilityLabel="To"
                  autoCapitalize="words"
                  autoComplete="name-given"
                  autoFocus={true}
                  containerStyle={styles.inputContainer}
                  errorMessageWhenTouched={errors.recipientName?.message}
                  inputStyle={styles.inputText}
                  keyboardType="default"
                  label="To"
                  labelStyle={styles.inputLabel}
                  maxLength={100}
                  onBlur={handleFieldBlur('recipientName')}
                  onChangeText={(text: string) =>
                    setValue('recipientName', text, {shouldValidate: true})
                  }
                  onSubmitEditing={() => noteInput.current?.focus()}
                  placeholder="Recipient Name"
                  testID="gift-card-shop-recipient-name-input"
                  touched={!!touchedFields.recipientName}
                  value={values.recipientName}
                />
                <View style={styles.inputContainer}>
                  <TextInput
                    accessibilityLabel="Message"
                    errorMessageWhenTouched={errors.note?.message}
                    inputStyle={[styles.inputText, Platform.OS === 'web' && {resize: 'auto'}]}
                    keyboardType="default"
                    label="Message"
                    labelStyle={styles.inputLabel}
                    maxLength={500}
                    multiline={true}
                    numberOfLines={Platform.OS === 'web' ? 6 : 2}
                    onBlur={handleFieldBlur('note')}
                    onChangeText={(text: string) => setValue('note', text, {shouldValidate: true})}
                    onSubmitEditing={() => emailInput.current?.focus()}
                    placeholder="Add your message..."
                    ref={noteInput}
                    testID="gift-card-shop-message-input"
                    touched={!!touchedFields.note}
                    value={values.note}
                  />
                  <Text variant="caption" style={styles.charCounter}>
                    {values.note.length}/500
                  </Text>
                </View>
              </View>
              <Title>3. DELIVERY DETAILS</Title>
              <Text variant="body" style={{marginBottom: 16}}>
                Your Gift Card will be delivered to the recipient by email on the selected delivery
                date (emails are sent from {GIFT_CARD_DELIVERY_TIME} on the selected date). If you
                choose today&apos;s date, the Gift Card will be sent immediately.
              </Text>
              <View style={styles.formSection}>
                <TextInput
                  accessibilityLabel="Email"
                  autoCapitalize="none"
                  autoComplete="email"
                  containerStyle={styles.inputContainer}
                  errorMessageWhenTouched={errors.recipientEmail?.message}
                  inputStyle={styles.inputText}
                  keyboardType="email-address"
                  label="Email"
                  labelStyle={styles.inputLabel}
                  maxLength={100}
                  onBlur={handleFieldBlur('recipientEmail')}
                  onChangeText={(text: string) =>
                    setValue('recipientEmail', text, {shouldValidate: true})
                  }
                  onSubmitEditing={() => phoneInput.current?.focus()}
                  placeholder="Recipient Email"
                  ref={emailInput}
                  testID="gift-card-shop-email-input"
                  touched={!!touchedFields.recipientEmail}
                  value={values.recipientEmail}
                />
                <TextInput
                  accessibilityLabel="Mobile Number"
                  autoCapitalize="none"
                  autoComplete="tel"
                  containerStyle={styles.inputContainer}
                  errorMessageWhenTouched={errors.recipientPhoneNumber?.message}
                  inputStyle={styles.inputText}
                  keyboardType="phone-pad"
                  label="Mobile Number"
                  labelStyle={styles.inputLabel}
                  maxLength={20}
                  onBlur={handleFieldBlur('recipientPhoneNumber')}
                  onChangeText={(text: string) =>
                    setValue('recipientPhoneNumber', text, {shouldValidate: true})
                  }
                  placeholder="Recipient Mobile Number"
                  ref={phoneInput}
                  testID="gift-card-shop-mobile-input"
                  touched={!!touchedFields.recipientPhoneNumber}
                  value={values.recipientPhoneNumber}
                />
                <View style={styles.inputContainer}>
                  <TmpDatePicker
                    label="Delivery Date"
                    value={values.deliveryDate}
                    onChange={(date: Date) => {
                      setNativeDatePickerModalOpen(false);
                      setValue('deliveryDate', date, {shouldValidate: true, shouldTouch: true});
                    }}
                    onNativePressIn={() => setNativeDatePickerModalOpen(true)}
                    nativeText={values.deliveryDate?.toLocaleDateString('en-AU') || 'Delivery date'}
                    isNativeDialogOpen={isNativeDatePickerModalOpen}
                    onCancel={() => setNativeDatePickerModalOpen(false)}
                    error={errors.deliveryDate?.message}
                    nativeMinimumDate={getTodayAt0Hours()}
                    testID="gift-card-shop-delivery-date-picker"
                  />
                </View>
              </View>
              <Button
                onPress={handleSubmit(handleFormSubmit)}
                title="NEXT"
                containerStyle={styles.nextButtonContainer}
                buttonStyle={styles.nextButton}
                loading={isUpdatingRecipientData}
                disabled={isUpdatingRecipientData}
                testID="gift-card-shop-next-button"
              />
            </View>
          )}
          <ErrorDialog
            isVisible={!!submitErrorMessage}
            onOk={handleErrorDialogOk}
            message={submitErrorMessage}
          />
        </KeyboardAwareScrollView>
      </ContentView>
    );
  },
);

export default GiftCardStoreShopScreen;

const styles = StyleSheet.create({
  formSection: {
    width: '100%',
  },
  inputContainer: {
    marginBottom: 8,
  },
  inputLabel: {
    marginBottom: 4,
  },
  inputText: {
    fontSize: 16,
  },
  charCounter: {
    textAlign: 'right',
    marginTop: -8,
    marginBottom: 8,
    color: '#666',
  },
  bannerImage: {
    width: '100%',
    height: undefined,
    aspectRatio: 3 / 2,
  },
  topSectionCopy: {
    marginTop: 16,
    marginBottom: 8,
  },
  activityIndicator: {
    marginTop: 16,
  },
  cardSelector: {
    width: '100%',
    marginBottom: 16,
  },
  nextButtonContainer: {
    marginTop: 24,
    marginBottom: 24,
  },
  nextButton: {
    height: 52,
  },
});
